import type { IClosingCostSetup } from 'pages/Admin/AdminTools/Configuration/HUD1/ClosingCostSetup/type'

import type { IFeeData } from './types'

function parseNum(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  return Number.isFinite(n) ? n : null
}

/** HUD1 min/max config: 0 means "not set" (same as empty). */
function parseConstraintLimit(v: unknown): number | null {
  const n = parseNum(v)
  if (n == null || n === 0) return null
  return n
}

/** Effective bounds: min = max(only configured minimums); max = min(only configured maximums). */
export function getEffectiveMinMax(setup: IClosingCostSetup, loanAmount: number) {
  const loan = Number(loanAmount) || 0
  const minAmt = parseConstraintLimit(setup.minChargeAmount)
  const minPct = parseConstraintLimit(setup.minChargePercentage)
  const maxAmt = parseConstraintLimit(setup.maxChargeAmount)
  const maxPct = parseConstraintLimit(setup.maxChargePercentage)

  const minCandidates: number[] = []
  if (minAmt != null) minCandidates.push(minAmt)
  if (minPct != null && loan >= 0) minCandidates.push((minPct / 100) * loan)

  const maxCandidates: number[] = []
  if (maxAmt != null) maxCandidates.push(maxAmt)
  if (maxPct != null && loan >= 0) maxCandidates.push((maxPct / 100) * loan)

  const minBound = minCandidates.length ? Math.max(...minCandidates) : null
  const maxBound = maxCandidates.length ? Math.min(...maxCandidates) : null

  return { minBound, maxBound }
}

export function validateFeeAmount(amount: number, setup: IClosingCostSetup | undefined, loanAmount: number): string {
  if (!setup?.addToClosingCalendar) return ''

  const minAmt = parseConstraintLimit(setup.minChargeAmount)
  const minPct = parseConstraintLimit(setup.minChargePercentage)
  const maxAmt = parseConstraintLimit(setup.maxChargeAmount)
  const maxPct = parseConstraintLimit(setup.maxChargePercentage)

  const loan = Number(loanAmount) || 0

  // Minimum: only if at least one of amount / percentage is set
  if (minAmt != null || minPct != null) {
    const minCandidates: number[] = []
    if (minAmt != null) minCandidates.push(minAmt)
    if (minPct != null) minCandidates.push((minPct / 100) * loan)
    const minBound = Math.max(...minCandidates)
    if (amount < minBound) {
      return `Amount must be at least $${minBound.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (closing cost setup limits).`
    }
  }

  // Maximum: only if at least one of amount / percentage is set
  if (maxAmt != null || maxPct != null) {
    const maxCandidates: number[] = []
    if (maxAmt != null) maxCandidates.push(maxAmt)
    if (maxPct != null) maxCandidates.push((maxPct / 100) * loan)
    const maxBound = Math.min(...maxCandidates)
    if (amount > maxBound) {
      return `Amount must be at most $${maxBound.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (closing cost setup limits).`
    }
  }

  return ''
}

export function buildDefaultClosingCalendarFees(setups: IClosingCostSetup[]): IFeeData[] {
  return setups
    .filter((s) => s.addToClosingCalendar)
    .sort((a, b) => a.lineId - b.lineId)
    .map((s) => ({
      lineId: s.lineId,
      title: s.lineName?.trim() ? s.lineName : `Line ${s.lineId}`,
      amount: 0,
    }))
}

/** Attach lineId to legacy fee rows when title matches a configured closing-calendar line. */
export function mergeSavedFeesWithConfig(saved: IFeeData[] | undefined, setups: IClosingCostSetup[]): IFeeData[] {
  if (!saved?.length) return buildDefaultClosingCalendarFees(setups)
  return saved.map((fee) => {
    if (fee.lineId != null) return fee
    const byTitle = setups.find((s) => s.addToClosingCalendar && s.lineName === fee.title)
    return byTitle ? { ...fee, lineId: byTitle.lineId } : fee
  })
}

export function validateClosingCalendarFees(
  fees: IFeeData[],
  setups: IClosingCostSetup[],
  loanAmount: number,
): string | null {
  const byLineId = new Map(setups.map((s) => [s.lineId, s]))
  for (const fee of fees) {
    const amt = parseNum(fee.amount)
    if (amt == null || !Number.isFinite(amt)) continue
    const setup = fee.lineId != null ? byLineId.get(fee.lineId) : undefined
    const err = validateFeeAmount(amt, setup, loanAmount)
    if (err) return `${fee.title || 'Fee'}: ${err}`
  }
  return null
}
